/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// @mui material components
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDSnackbar from 'components/MDSnackbar';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

import SearchPr from './SearchPr';
import SearchProduct from './SearchProduct';
import ExportRequestStock from './ExportRequestStock';
import ExportRequestText from './ExportRequestText';
import SearchPoint from './SearchPoint';

const initParent = {
  id: '',
  code: '',
  remark: '',
  status: 'edit',
  print_url: '',
  created_at: '',
  updated_at: '',
};

const initPhoto = {
  id: '',
  code: '',
  sku: '',
  name: '',
  image: '',
};

function ExportRequestList() {
  const mod = 'export_request_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parent, setParent] = useState(initParent);
  const [modalPhoto, setModalPhoto] = useState(false);
  const [photo, setPhoto] = useState(initPhoto);
  const [modalError, setModalError] = useState(null);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = () => {
    setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        setRecord(result.data);
      }
      setLoading(false);
    });
  };

  const resolveErrorMsg = (msg) => {
    if (msg && typeof msg === 'object') {
      const { msg: code, data } = msg;
      if (code === 'quantity_min') {
        return `จำนวนสินค้าต้องไม่น้อยกว่า ${data?.required_min ?? data?.min} ${
          data?.unit ?? ''
        }`.trim();
      }
      if (code === 'quantity_max') {
        return `จำนวนสินค้าต้องไม่เกิน ${data?.required_max ?? data?.max} ${
          data?.unit ?? ''
        }`.trim();
      }
      return code ?? JSON.stringify(msg);
    }
    return msg;
  };

  const showErrorModal = (msg) => {
    if (msg && typeof msg === 'object') {
      const { msg: code, data } = msg;
      if (code === 'quantity_min' || code === 'quantity_max') {
        setModalError({ code, data });
        return true;
      }
    }
    return false;
  };

  const add = (item) => {
    setLoading(true);

    return FetchData('PUT', `/${mod}`, app.token, item).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.addcomplete`), 'success');

        if (result.check === 'N') {
          showNotify('ปริมาณสินค้าไม่ถูกต้อง', 'error');
        }
        return true;
      }

      if (!showErrorModal(result.msg)) {
        showNotify(resolveErrorMsg(result.msg), 'error');
      }
      return false;
    });
  };

  const openPhoto = (row) => {
    setPhoto(row);
    setModalPhoto(true);
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        view();
      } else {
        Func.exitApp();
      }
    });
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MDBox p={2}>
              <MDTypography variant='h5'>{t(`element.order`)}</MDTypography>
            </MDBox>
          </Grid>
          {parent.status !== 'edit' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1} p={2}>
                <SearchPoint add={add} />
                <SearchPr add={add} />
                <SearchProduct add={add} />
                <ExportRequestText callback={view} />
              </MDBox>
            </Grid>
          )}
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 400 }}>
              <Table aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' width={10}>
                      &nbsp;
                    </TableCell>
                    <TableCell align='center' width={30}>
                      {t(`attr.id`)}
                    </TableCell>
                    <TableCell align='center' width={40}>
                      {t(`attr.image`)}
                    </TableCell>
                    <TableCell>สินค้า</TableCell>
                    <TableCell width={100}>ประเภทยา</TableCell>
                    <TableCell align='right' width={100}>
                      จำนวน
                    </TableCell>
                    <TableCell align='right' width={100}>
                      หยิบแล้ว
                    </TableCell>
                    <TableCell align='right' width={100}>
                      ตรวจแล้ว
                    </TableCell>
                    <TableCell align='right' width={100}>
                      รับเข้า
                    </TableCell>
                    <TableCell width={50}>หน่วย</TableCell>
                    <TableCell width={10}>&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                {record.map((row) => (
                  <ExportRequestStock
                    parent={parent}
                    row={row}
                    view={view}
                    openPhoto={openPhoto}
                    key={row.id}
                  />
                ))}
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderErrorModal = () => {
    if (!modalError) return null;

    const { code, data } = modalError;
    const isMin = code === 'quantity_min';
    const limit = isMin ? data?.required_min ?? data?.min : data?.required_max ?? data?.max;
    const unit = data?.unit ?? '';

    return (
      <Dialog
        open={Boolean(modalError)}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-error'
        fullWidth
        onClose={() => setModalError(null)}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          <MDBox display='flex' alignItems='center' gap={1}>
            <Icon color='error'>error_outline</Icon>
            {isMin ? 'จำนวนสินค้าน้อยเกินไป' : 'จำนวนสินค้ามากเกินไป'}
          </MDBox>
          <IconButton
            aria-label='close'
            onClick={() => setModalError(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={2} textAlign='center'>
            <MDTypography variant='body2' color='text'>
              {isMin ? 'จำนวนขั้นต่ำที่ต้องการ' : 'จำนวนสูงสุดที่อนุญาต'}
            </MDTypography>
            <MDTypography variant='h3' color='error' mt={1}>
              {limit} {unit}
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            variant='contained'
            color='error'
            onClick={() => {
              setModalError(null);
              view();
            }}
          >
            ตกลง
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.code} <span className='color-default'>{photo.sku}</span>
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={1}>
            <MDTypography variant='body'>{photo.name}</MDTypography>
          </MDBox>
          <MDBox
            component='img'
            src={photo.image}
            alt=''
            borderRadius='lg'
            width='100%'
            position='relative'
            sx={{ boxShadow: 0 }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <div>
      {renderList()}
      {renderErrorModal()}
      {renderPhoto()}
      {notify}
    </div>
  );
}

export default ExportRequestList;
